/**
 * The synthesis engine.
 *
 * A faithful port of the generator that already scores the game, kept faithful
 * on purpose: the first thing this project has to prove is that it can
 * reproduce an existing track sample for sample. If it cannot, nothing built on
 * top of it can be trusted to sound like the game does today.
 *
 * Written once and run in two places. In Node it writes stem packs for the
 * game; in the browser it drives Web Audio for the editor. Two synthesisers
 * would drift, and then the preview would lie about what ships.
 *
 * Everything here is pure arithmetic over a Float64Array. No Web Audio, no
 * Node APIs, nothing environment-specific, which is what makes that possible.
 */

export const SR = 44100;
const TWO_PI = Math.PI * 2;

/** Frequencies above this get harsh on small speakers. Ported deliberately. */
export const MAX_FREQ = 1400;

// ---- notes -----------------------------------------------------------------

const NOTE_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

/**
 * Note name to semitones from A4. Built once.
 *
 * The original covered octaves 1 to 7, which was enough for hand-written
 * tracks. Generated ones reach further: a melody layer builds its pool two
 * octaves above its own setting, so an octave 5 melody in a high key asks for
 * notes in octave 8 and got `unknown note: Eb8` in the middle of a render.
 *
 * Widening the table cannot change any existing render, because every name
 * that resolved before resolves to the same number now, and `noteFreq` still
 * clamps to MAX_FREQ. It only stops the throw.
 */
const SEMITONES: Record<string, number> = {};
for (let octave = 0; octave < 10; octave++) {
  NOTE_NAMES.forEach((name, i) => {
    SEMITONES[`${name}${octave}`] = (octave - 4) * 12 + (i - 9);
  });
}

export function noteFreq(note: string): number {
  const semi = SEMITONES[note];
  if (semi === undefined) throw new Error(`unknown note: ${note}`);
  return Math.min(MAX_FREQ, 440 * Math.pow(2, semi / 12));
}

// ---- oscillators -----------------------------------------------------------

export type WaveType =
  | "sin" | "tri" | "bell" | "organ" | "string"
  | "glass" | "warm" | "harp" | "flute" | "celeste"
  | "noise" | "kick" | "square";

/**
 * One sample of a waveform at a given phase.
 *
 * `progress` is how far through the note we are, 0 to 1. Only the plucked
 * voices use it, to damp their upper partials as the note decays, which is
 * what stops a harp sounding like an organ.
 */
function waveSample(type: WaveType, phase: number, progress: number): number {
  const s = (mult: number) => Math.sin(TWO_PI * phase * mult);
  switch (type) {
    case "tri":
      return 4 * Math.abs(((phase % 1) + 1) % 1 - 0.5) - 1;
    case "bell":
      return s(1) * 0.55 + s(2.01) * 0.25 + s(3) * 0.12 + s(4.01) * 0.05;
    case "organ":
      return s(1) * 0.5 + s(2) * 0.25 + s(3) * 0.15 + s(4) * 0.07;
    case "string":
      return s(1) * 0.55 + s(2) * 0.25 + s(3) * 0.12 + s(4) * 0.05;
    case "glass":
      return s(1) * 0.6 + s(2) * 0.25 + s(4) * 0.1;
    case "warm":
      return s(1) * 0.7 + s(2) * 0.2 + s(3) * 0.07;
    case "harp": {
      const damp = Math.exp(-3 * progress);
      return s(1) * 0.5 + s(2) * 0.3 * damp + s(3) * 0.1 * damp;
    }
    case "flute":
      return s(1) * 0.75 + s(2) * 0.2;
    case "celeste": {
      const damp = Math.exp(-2 * progress);
      return s(1) * 0.5 * damp + s(2) * 0.35 + s(4) * 0.08 * damp;
    }
    case "square":
      // Hard edged and cheap. Two harmonics short of a real square, which
      // keeps it from tearing at the top of the range where MAX_FREQ bites.
      return s(1) * 0.6 + s(3) * 0.2 + s(5) * 0.12 + s(7) * 0.06;
    case "noise": {
      // Deterministic hash of the phase rather than Math.random. Everything in
      // this project is reproducible from a seed, and a drum that came out
      // different every render would quietly break that promise for the whole
      // piece rather than just the hats.
      const x = Math.sin(phase * 127.1 + 311.7) * 43758.5453;
      return (x - Math.floor(x)) * 2 - 1;
    }
    case "kick": {
      // The pitch drop is what makes a kick a kick rather than a thud, and the
      // engine has no pitch envelope, so it is done here against `progress`.
      const bend = Math.exp(-16 * progress);
      const body = Math.sin(TWO_PI * phase * (0.4 + bend * 2.4));
      // A short burst of noise at the very start reads as the beater.
      const click = progress < 0.02 ? (Math.sin(phase * 419.3) % 1) * 0.25 : 0;
      return body * 0.92 + click;
    }
    default:
      return s(1);
  }
}

export interface ToneOptions {
  wave?: WaveType;
  attack?: number;
  decay?: number;
  sustain?: number;
  release?: number;
  detune?: number;
  vibratoRate?: number;
  vibratoDepth?: number;
  lpf?: number;
}

/**
 * Add one enveloped tone into a buffer.
 *
 * The envelope is sample-counted rather than time-based, and when a note is too
 * short to fit attack, decay and release, the release is what gets shortened.
 * That is the original's behaviour and it matters: changing it would alter
 * every short rhythm hit in the existing tracks.
 */
export function addTone(
  buf: Float64Array,
  freqHz: number,
  start: number,
  dur: number,
  vol: number,
  opts: ToneOptions = {},
): void {
  const {
    wave = "sin", attack = 0.01, decay = 0.1, sustain = 0.7, release = 0.3,
    detune = 0, vibratoRate = 0, vibratoDepth = 0, lpf = 0,
  } = opts;

  const freq = Math.min(freqHz, MAX_FREQ);
  const total = buf.length;
  const i0 = Math.floor(start * SR);
  if (i0 >= total) return;

  let count = Math.floor(dur * SR);
  count = Math.min(count, total - i0);
  if (count <= 0) return;

  const attackSamples = Math.floor(attack * SR);
  const decaySamples = Math.floor(decay * SR);
  let releaseSamples = Math.floor(release * SR);
  const hold = Math.max(0, count - attackSamples - decaySamples - releaseSamples);
  if (hold === 0) releaseSamples = Math.max(0, count - attackSamples - decaySamples);

  const detunedFreq = detune ? freq * (1 + detune) : 0;
  const smooth = Math.max(0, Math.min(0.99, lpf));
  let prev = 0;

  for (let i = 0; i < count; i++) {
    let env: number;
    if (i < attackSamples) {
      env = attackSamples > 0 ? i / attackSamples : 1;
    } else if (i < attackSamples + decaySamples) {
      env = decaySamples > 0
        ? 1 - (1 - sustain) * ((i - attackSamples) / decaySamples)
        : sustain;
    } else if (i < attackSamples + decaySamples + hold) {
      env = sustain;
    } else {
      const ri = i - attackSamples - decaySamples - hold;
      env = releaseSamples > 0 ? sustain * (1 - ri / releaseSamples) : 0;
    }

    const t = i / SR;
    const vib = vibratoRate > 0 ? Math.sin(TWO_PI * vibratoRate * t) * vibratoDepth : 0;
    const phase = freq * (1 + vib) * t;

    let val = waveSample(wave, phase, i / count);

    if (detune) {
      val = (val + Math.sin(TWO_PI * detunedFreq * (1 + vib) * t)) * 0.5;
    }

    if (smooth > 0) {
      val = prev * smooth + val * (1 - smooth);
      prev = val;
    }

    buf[i0 + i] += val * env * vol;
  }
}

// ---- instruments -----------------------------------------------------------

export interface InstrumentLayer extends ToneOptions {
  volMult: number;
  octaveShift?: number;
}

export interface Instrument {
  /** "chord" plays every note it is given; "single" plays only the first. */
  type: "chord" | "single";
  layers: InstrumentLayer[];
}

export const INSTRUMENTS: Record<string, Instrument> = {
  glass_pad: {
    type: "chord",
    layers: [{ wave: "glass", volMult: 1.0, attack: 3.0, decay: 1.0, sustain: 0.35, release: 3.0, lpf: 0.2 }],
  },
  warm_pad: {
    type: "chord",
    layers: [
      { wave: "organ", volMult: 0.6, attack: 2.5, decay: 1.0, sustain: 0.5, release: 2.5, lpf: 0.3 },
      { wave: "sin", volMult: 0.3, attack: 3.0, decay: 0.5, sustain: 0.4, release: 2.0 },
    ],
  },
  bell: {
    type: "single",
    layers: [{ wave: "bell", volMult: 1.0, attack: 0.01, decay: 0.2, sustain: 0.35, release: 0.5, lpf: 0.15 }],
  },
  celeste: {
    type: "single",
    layers: [{ wave: "celeste", volMult: 1.0, attack: 0.005, decay: 0.15, sustain: 0.25, release: 0.5 }],
  },
  bass: {
    type: "single",
    layers: [
      { wave: "sin", volMult: 0.7, attack: 0.1, decay: 0.3, sustain: 0.45, release: 0.6, lpf: 0.4 },
      { wave: "warm", volMult: 0.2, attack: 0.15, decay: 0.2, sustain: 0.35, release: 0.5, lpf: 0.3 },
    ],
  },
  deep_bass: {
    type: "single",
    layers: [{ wave: "sin", volMult: 0.8, attack: 0.2, decay: 0.4, sustain: 0.5, release: 0.8, lpf: 0.5 }],
  },

  // Everything below was added once the port was proven. The six above are the
  // originals and their settings must not be touched: the A/B test renders the
  // shipped track through them, and a nudged envelope would break it.
  //
  // The wave shapes were all already in the engine and unused, which is why a
  // generator with six instruments sounded like one instrument in six moods.

  string_pad: {
    type: "chord",
    layers: [
      // Two voices a few cents apart. The beating between them is what stops a
      // sustained chord sounding like a held oscillator, and it costs nothing.
      { wave: "string", volMult: 0.55, attack: 1.8, decay: 1.2, sustain: 0.55, release: 2.4, lpf: 0.25, detune: 0.004 },
      { wave: "sin", volMult: 0.25, attack: 2.4, decay: 0.8, sustain: 0.45, release: 2.0 },
    ],
  },
  organ_pad: {
    type: "chord",
    layers: [{ wave: "organ", volMult: 0.9, attack: 0.9, decay: 0.6, sustain: 0.7, release: 1.4, lpf: 0.15 }],
  },
  air_pad: {
    type: "chord",
    layers: [
      { wave: "flute", volMult: 0.7, attack: 3.5, decay: 1.5, sustain: 0.4, release: 3.5, lpf: 0.35, vibratoRate: 0.3, vibratoDepth: 0.002 },
    ],
  },
  flute: {
    type: "single",
    layers: [
      // Named because `approaches.ts` has defaulted its melody to "flute" since
      // the port, against an instrument that did not exist. Any track with a
      // melody and no explicit instrument threw at render time.
      { wave: "flute", volMult: 1.0, attack: 0.12, decay: 0.15, sustain: 0.8, release: 0.35, vibratoRate: 5, vibratoDepth: 0.004 },
    ],
  },
  harp: {
    type: "single",
    layers: [{ wave: "harp", volMult: 1.0, attack: 0.004, decay: 0.45, sustain: 0.12, release: 0.9 }],
  },
  pluck: {
    type: "single",
    layers: [{ wave: "tri", volMult: 0.85, attack: 0.003, decay: 0.12, sustain: 0.1, release: 0.3, lpf: 0.25 }],
  },
  chime: {
    type: "single",
    layers: [{ wave: "bell", volMult: 0.9, attack: 0.002, decay: 0.5, sustain: 0.1, release: 1.6, lpf: 0.05 }],
  },
  glass_bell: {
    type: "single",
    layers: [{ wave: "glass", volMult: 0.9, attack: 0.01, decay: 0.3, sustain: 0.2, release: 0.9, lpf: 0.1 }],
  },
  warm_bass: {
    type: "single",
    layers: [
      { wave: "warm", volMult: 0.75, attack: 0.06, decay: 0.35, sustain: 0.5, release: 0.5, lpf: 0.35 },
      { wave: "tri", volMult: 0.15, attack: 0.08, decay: 0.25, sustain: 0.3, release: 0.4, lpf: 0.45 },
    ],
  },
  pluck_bass: {
    type: "single",
    layers: [{ wave: "harp", volMult: 0.8, attack: 0.004, decay: 0.3, sustain: 0.25, release: 0.6, lpf: 0.4 }],
  },
};

/**
 * Drums.
 *
 * Synthesised from the same oscillators as everything else rather than sampled,
 * so the whole project stays one file of arithmetic with no assets to ship and
 * nothing to license. They are pitched like any other instrument, which is why
 * the patterns name a note: retuning a kit is a data change.
 */
export const DRUMS: Record<string, Instrument> = {
  kick: {
    type: "single",
    layers: [{ wave: "kick", volMult: 1.0, attack: 0.001, decay: 0.09, sustain: 0.25, release: 0.09 }],
  },
  snare: {
    type: "single",
    layers: [
      { wave: "noise", volMult: 0.55, attack: 0.001, decay: 0.07, sustain: 0.12, release: 0.11 },
      { wave: "tri", volMult: 0.3, attack: 0.001, decay: 0.05, sustain: 0.1, release: 0.07 },
    ],
  },
  hat: {
    type: "single",
    // No low pass at all. A closed hat is almost entirely the top end, and
    // smoothing it turns the kit into someone tapping a cushion.
    layers: [{ wave: "noise", volMult: 0.32, attack: 0.0005, decay: 0.025, sustain: 0.04, release: 0.03 }],
  },
  open_hat: {
    type: "single",
    layers: [{ wave: "noise", volMult: 0.26, attack: 0.001, decay: 0.09, sustain: 0.18, release: 0.16 }],
  },
  rim: {
    type: "single",
    layers: [{ wave: "square", volMult: 0.3, attack: 0.001, decay: 0.02, sustain: 0.05, release: 0.04, lpf: 0.1 }],
  },
  tom: {
    type: "single",
    layers: [{ wave: "kick", volMult: 0.7, attack: 0.002, decay: 0.14, sustain: 0.3, release: 0.16 }],
  },
  shaker: {
    type: "single",
    layers: [{ wave: "noise", volMult: 0.18, attack: 0.004, decay: 0.03, sustain: 0.06, release: 0.04 }],
  },
};

for (const [name, instrument] of Object.entries(DRUMS)) INSTRUMENTS[name] = instrument;

/** Instruments that make sense per layer, for randomising and for the editor. */
export const INSTRUMENTS_BY_ROLE: Record<string, string[]> = {
  pad: ["glass_pad", "warm_pad", "string_pad", "organ_pad", "air_pad"],
  bass: ["bass", "deep_bass", "warm_bass", "pluck_bass"],
  rhythm: ["bell", "celeste", "pluck", "harp", "glass_bell", "chime"],
  melody: ["celeste", "flute", "harp", "chime", "bell", "glass_bell"],
  // Percussion picks its instruments from the pattern, not from a setting, so
  // this exists only so the editor has something to show.
  perc: Object.keys(DRUMS),
};

export function playInstrument(
  buf: Float64Array,
  instName: string,
  notes: string | string[],
  start: number,
  dur: number,
  vol: number,
): void {
  const inst = INSTRUMENTS[instName];
  if (!inst) throw new Error(`unknown instrument: ${instName}`);

  let list = typeof notes === "string" ? [notes] : notes;
  if (inst.type === "single") list = list.slice(0, 1);

  for (const note of list) {
    const freq = noteFreq(note);
    for (const layer of inst.layers) {
      let f = freq;
      if (layer.octaveShift) f *= Math.pow(2, layer.octaveShift);
      addTone(buf, Math.min(f, MAX_FREQ), start, dur, vol * layer.volMult, layer);
    }
  }
}

// ---- mastering -------------------------------------------------------------

export function applyFades(buf: Float64Array, fadeIn: number, fadeOut: number): void {
  const total = buf.length;
  const fi = Math.floor(fadeIn * SR);
  const fo = Math.floor(fadeOut * SR);
  for (let i = 0; i < Math.min(fi, total); i++) buf[i] *= i / fi;
  for (let i = 0; i < Math.min(fo, total); i++) {
    const idx = total - fo + i;
    if (idx >= 0 && idx < total) buf[idx] *= 1 - i / fo;
  }
}

/**
 * Peak normalise.
 *
 * Deliberately peak rather than loudness: stems have to be mixed together at
 * runtime, and anything that changes gain per layer based on its own content
 * would make the mix depend on which layers happen to be audible.
 */
export function normalize(buf: Float64Array, peakLevel = 0.65): void {
  let peak = 0;
  for (const s of buf) peak = Math.max(peak, Math.abs(s));
  if (peak === 0) return;
  const gain = peakLevel / peak;
  for (let i = 0; i < buf.length; i++) buf[i] *= gain;
}

export function makeBuffer(duration: number): Float64Array {
  return new Float64Array(Math.floor(duration * SR));
}
